import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { grid, distances } from '../../../styles/constants';
import { Transaction, TransactionEvent } from '../../../types/checkout';
import { P } from '../../../components/Typography';
import { Button, ButtonGroup } from '../../../components/Buttons';
import { formatExpirationDate } from '../../transactionHelpers';

type ExtendFormProps = {
    transaction: Transaction;
    extendTransaction: (accountId: string, transaction: Transaction) => Promise<Transaction | undefined>;
    accountId: string;
    isLoading: boolean;
    showSuccessMessage: () => void;
};

const ExtendForm = ({
    transaction,
    extendTransaction,
    accountId,
    isLoading,
    showSuccessMessage,
}: ExtendFormProps) => {
    const { t } = useTranslation();

    const successfulAuthorizationEvents = transaction.payment_product === 'klarna'
        ? transaction.events.filter(
            (event: TransactionEvent) =>
                event.event === 'AUTHORIZE' && event.success
        )
        : [];

    const newestAuthorizationEvent = successfulAuthorizationEvents.length > 0
        ? successfulAuthorizationEvents[successfulAuthorizationEvents.length - 1]
        : undefined;

    if (newestAuthorizationEvent && newestAuthorizationEvent.metadata && newestAuthorizationEvent.metadata['klarna:authorization_expiration']) {
        const originalExpirationDate = new Date(newestAuthorizationEvent.metadata['klarna:authorization_expiration']);

        // The new expiration date should be the original date + 28 days
        const newExpirationDate = new Date(originalExpirationDate);
        newExpirationDate.setDate(newExpirationDate.getDate() + 28);

        const formattedOriginalExpirationDate = formatExpirationDate(originalExpirationDate);
        const formattedNewExpirationDate = formatExpirationDate(newExpirationDate);

        return (
            <Wrapper>
                <ExtendTextWrapper>
                    <ExtendP>{t('payments.extendTransaction.form.explanation')}</ExtendP>
                    <ExtendP>
                        <Trans
                            i18nKey="payments.extendTransaction.form.consequence"
                            values={{
                                transaction_id: transaction.merchant_reference || transaction.id,
                                days: 28,
                            }}
                        >
                                Text
                            <strong>{{ transaction_id: transaction.merchant_reference || transaction.id }}</strong>
                        </Trans>
                    </ExtendP>
                    <ExtendP>
                        <Trans
                            i18nKey="payments.extendTransaction.form.result"
                            values={{
                                current_expiration_date: formattedOriginalExpirationDate,
                                new_expiration_date: formattedNewExpirationDate,
                            }}
                        >
                            Text
                            <strong>{{ new_expiration_date: formattedNewExpirationDate }}</strong>
                        </Trans>
                    </ExtendP>
                </ExtendTextWrapper>
                <ButtonGroup>
                    <ExtendButton
                        disabled={isLoading}
                        onClick={() => {
                            extendTransaction(accountId, transaction).then(result => {
                                if (result) {
                                    showSuccessMessage();
                                }
                            });
                        }}
                    >
                        {t('payments.extendTransaction.form.button')}
                    </ExtendButton>
                </ButtonGroup>
            </Wrapper>
        );
    };

    return (
        <Wrapper>
            <ExtendTextWrapper>
                <ExtendP>{t('payments.extendTransaction.form.explanation')}</ExtendP>
                <ExtendP>
                    <Trans
                        i18nKey="payments.extendTransaction.form.consequence"
                        values={{
                            transaction_id: transaction.merchant_reference || transaction.id,
                            days: 28,
                        }}
                    >
                            Text
                        <strong>{{ transaction_id: transaction.merchant_reference || transaction.id }}</strong>
                    </Trans>
                </ExtendP>
            </ExtendTextWrapper>
            <ButtonGroup>
                <ExtendButton
                    disabled={isLoading}
                    onClick={() => {
                        extendTransaction(accountId, transaction).then(result => {
                            if (result) {
                                showSuccessMessage();
                            }
                        });
                    }}
                >
                    {t('payments.extendTransaction.form.button')}
                </ExtendButton>
            </ButtonGroup>
        </Wrapper>
    );
};

export default ExtendForm;

const Wrapper = styled.div`
    position: relative;
    width: ${grid.spans.span6};
`;

const ExtendTextWrapper = styled.div`
    margin-top: ${distances.small};
    margin-bottom: ${distances.small};
`;

const ExtendP = styled(P)`
    margin-bottom: ${distances.small};
    line-height: ${distances.small};
`;

const ExtendButton = styled(Button)`
    min-width: 220px;
`;
